import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Star } from "lucide-react";
import { SITE } from "../../site.config";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 pt-20 pb-10 overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-pz/60 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <img
              src={SITE.business.logo}
              alt={SITE.business.fullName}
              className="h-40 md:h-48 lg:h-56 w-auto mb-6 drop-glow-cyan"
            />
            <p className="text-white/60 barlow text-sm leading-relaxed mb-6">
              {SITE.business.fullName}. Sunshine Coast's trusted auto
              electrical specialists for 4WDs, towing electrics, diagnostics &
              auto air conditioning.
            </p>
            <div className="flex items-center gap-3">
              {SITE.business.facebook && (
                <a
                  href={SITE.business.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="footer-fb"
                  className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/70 hover:border-cyan-pz hover:text-cyan-pz transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              <a
                href={SITE.business.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Instagram ${SITE.business.instagramHandle}`}
                data-testid="footer-ig"
                className="inline-flex items-center gap-2 border border-white/20 px-3 h-10 text-white/70 hover:border-cyan-pz hover:text-cyan-pz transition-colors group"
              >
                <Instagram className="w-4 h-4" />
                <span className="bebas tracking-widest text-sm">
                  {SITE.business.instagramHandle}
                </span>
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="bebas text-cyan-pz tracking-[0.25em] text-sm mb-5">
              SERVICES
            </h4>
            <ul className="space-y-3">
              {SITE.services.map((s) => (
                <li key={s.key}>
                  <Link
                    to={s.href}
                    data-testid={`footer-service-${s.key}`}
                    className="text-white/70 barlow text-sm hover:text-cyan-pz transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="bebas text-cyan-pz tracking-[0.25em] text-sm mb-5">
              SERVICE AREA
            </h4>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-2" data-testid="footer-service-area">
              {SITE.serviceAreas.slice(0, 10).map((name) => (
                <li key={name} className="text-white/70 barlow text-sm">
                  {name}
                </li>
              ))}
            </ul>
            <Link
              to="/services/auto-electrician-sunshine-coast"
              data-testid="footer-service-area-link"
              className="inline-block mt-4 text-cyan-pz barlow text-sm hover:underline underline-offset-4"
            >
              All Sunshine Coast suburbs →
            </Link>
          </div>

          <div className="md:col-span-2">
            <h4 className="bebas text-cyan-pz tracking-[0.25em] text-sm mb-5">
              CONTACT
            </h4>
            <ul className="space-y-3 text-white/70 barlow text-sm">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-cyan-pz mt-0.5 shrink-0" />
                <a href={`tel:${SITE.business.phoneTel}`} className="hover:text-cyan-pz">
                  {SITE.business.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-cyan-pz mt-0.5 shrink-0" />
                <a
                  href={`mailto:${SITE.business.email}`}
                  className="hover:text-cyan-pz break-all"
                >
                  {SITE.business.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-cyan-pz mt-0.5 shrink-0" />
                <span>{SITE.business.address.full}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <p className="text-white/40 barlow text-xs">
            © {year} {SITE.business.fullName}. All rights reserved.
            <span className="block mt-1 text-white/40" data-testid="footer-legal-note">{SITE.business.legalNote}</span>
          </p>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <Link
              to="/policies"
              data-testid="footer-policies-link"
              className="inline-flex items-center gap-2 bebas text-white/70 hover:text-cyan-pz tracking-widest text-xs"
            >
              WORKSHOP POLICIES
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="inline-flex items-center gap-2 bebas text-white/70 hover:text-cyan-pz tracking-widest text-xs"
            >
              PRIVACY POLICY
            </Link>
            <Link
              to="/blog"
              data-testid="footer-blog-link"
              className="inline-flex items-center gap-2 bebas text-white/70 hover:text-cyan-pz tracking-widest text-xs"
            >
              BLOG
            </Link>
            <Link
              to="/reviews"
              data-testid="footer-reviews-link"
              className="inline-flex items-center gap-2 bebas text-cyan-pz/90 hover:text-cyan-pz tracking-widest text-xs"
            >
              <Star className="w-3.5 h-3.5 fill-cyan-pz text-cyan-pz" />
              READ ALL CUSTOMER REVIEWS
            </Link>
            <Link
              to="/review"
              data-testid="footer-review-link"
              className="inline-flex items-center gap-2 bebas text-cyan-pz/90 hover:text-cyan-pz tracking-widest text-xs"
            >
              <Star className="w-3.5 h-3.5 fill-cyan-pz text-cyan-pz" />
              LEAVE A GOOGLE REVIEW
            </Link>
            <p className="text-white/40 barlow text-xs">
              Auto Electrician • 4WD Accessories Fitter • Towing Electrics •
              Sunshine Coast QLD
            </p>
            <p
              data-testid="footer-abn"
              className="text-white/50 barlow text-xs"
            >
              ABN: <span className="text-cyan-pz/80">49 680 967 088</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
